import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTranslation } from '../LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Lock, User, Car, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';

const GOOGLE_AUTH_URL = 'https://auth.emergentagent.com/';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(formData.name, formData.email, formData.password);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const benefits = [
    'List your cars for free',
    'Access to VIN history checks',
    'Save favorite listings',
    'Get AI recommendations',
  ];

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 particle-bg">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-hero-glow pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card rounded-2xl p-8">
          <h1 className="font-heading text-2xl font-bold text-center mb-2">{t('auth.registerTitle')}</h1>
          <p className="text-gray-400 text-center mb-6">{t('auth.registerSubtitle')}</p>

          {/* Google Auth Button */}
          <button
            onClick={handleGoogleAuth}
            className="w-full h-11 flex items-center justify-center gap-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 hover:border-white/20 transition-all mb-5"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-sm font-medium text-gray-300">{t('auth.google')}</span>
          </button>

          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
            <div className="relative flex justify-center"><span className="px-3 text-xs text-gray-500 bg-[#0a0a0f]">{t('auth.or')}</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">{t('auth.name')}</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input type="text" name="name" placeholder={t('auth.name')} value={formData.name} onChange={handleChange}
                  className="pl-12 h-12 bg-black/50 border-white/10" data-testid="register-name-input" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input type="email" name="email" placeholder={t('auth.email')} value={formData.email} onChange={handleChange}
                  className="pl-12 h-12 bg-black/50 border-white/10" data-testid="register-email-input" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">{t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input type="password" name="password" placeholder={t('auth.password')} value={formData.password} onChange={handleChange}
                  className="pl-12 h-12 bg-black/50 border-white/10" data-testid="register-password-input" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Confirm {t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input type="password" name="confirmPassword" placeholder={`Confirm ${t('auth.password')}`} value={formData.confirmPassword} onChange={handleChange}
                  className="pl-12 h-12 bg-black/50 border-white/10" data-testid="register-confirm-password-input" />
              </div>
            </div>

            <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20">
              <p className="text-sm font-medium mb-2">With your account you get:</p>
              <ul className="space-y-1">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-400"><Check className="w-4 h-4 text-cyan-500" />{b}</li>
                ))}
              </ul>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-cyan-500 text-black hover:bg-cyan-400 font-bold shadow-neon hover:shadow-neon-lg transition-all"
              data-testid="register-submit-btn">
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                  {t('auth.registerBtn')}...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {t('auth.registerBtn')} <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-cyan-500 hover:underline font-medium">{t('nav.login')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
